import { ChangeDetectionStrategy, Component, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';

// A date and a time in one form control, for when a plain date picker is not enough (session times,
// check-in and check-out). The value is a wall-clock string with no timezone, 'YYYY-MM-DDTHH:mm', or ''
// until both a date and a time are chosen - the same shape the API takes and returns, so what was
// entered is what is shown back. Picking a date with no time yet fills in the default time.
@Component({
  selector: 'date-time-picker',
  imports: [...SharedMaterialUIModule],
  templateUrl: './date-time-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateTimePickerComponent),
      multi: true,
    },
  ],
})
export class DateTimePickerComponent implements ControlValueAccessor {
  readonly label = input('Date and time');
  readonly defaultTime = input('09:00');

  protected readonly date = signal<Date | null>(null);
  protected readonly time = signal('');
  protected readonly disabled = signal(false);

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: string | null): void {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})/.exec(value ?? '');
    if (!match) {
      this.date.set(null);
      this.time.set('');
      return;
    }

    this.date.set(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    this.time.set(match[4]);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected dateChanged(value: Date | null): void {
    this.date.set(value);
    if (value && !this.time()) {
      this.time.set(this.defaultTime());
    }

    this.emit();
  }

  protected timeChanged(value: string): void {
    this.time.set(value);
    this.emit();
  }

  protected touched(): void {
    this.onTouched();
  }

  private emit(): void {
    const date = this.date();
    const time = this.time();

    if (!date || !time) {
      this.onChange('');
      return;
    }

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    this.onChange(`${date.getFullYear()}-${month}-${day}T${time}`);
  }
}
